// Subdomain routing for the deployed apex.
//
// - The station editor is served from `<station-id>.workerstation.sandbox-medsimples.dev`.
// - Each workerspace worker is served from its OWN subdomain,
//   `<worker-name>--<station-id>.workerstation.sandbox-medsimples.dev`, at `/`.
//   `--` is the separator; it is forbidden in workerspace/worker names so the
//   split is unambiguous.
//
// A wildcard route (`*.workerstation.sandbox-medsimples.dev/*`) points every
// label at this Worker. Path-based routing stays available for localhost,
// synthetic origins, and nested (recursive) Workerstation instances, which
// cannot mint another subdomain level.

import { Option, Schema } from "effect";
import { StationId } from "./station-id.ts";
import { WorkerName } from "./worker-name.ts";

export const WORKERSTATION_APEX_HOST = "workerstation.sandbox-medsimples.dev";

/** Separator between a worker name and its station in a worker subdomain. */
export const WORKERSPACE_HOST_SEPARATOR = "--";

const ApexHost = /* @__PURE__ */ (() => Schema.Literal(WORKERSTATION_APEX_HOST))();
const isApex = /* @__PURE__ */ (() => Schema.is(ApexHost))();
export const isWorkerstationApexHost = (hostname: string): boolean => isApex(hostname.toLowerCase());

export const WorkerHostLabel = WorkerName;
export const VersionLabel = /* @__PURE__ */ (() => WorkerName.pipe(Schema.brand("version-label")))();
export type VersionLabel = typeof VersionLabel.Type;
export const isValidWorkerHostLabel: (value: string) => boolean = /* @__PURE__ */ (() => Schema.is(WorkerHostLabel))();
export const isValidVersionLabel: (value: string) => boolean = /* @__PURE__ */ (() => Schema.is(VersionLabel))();

/** What a workerstation hostname addresses, validated before namespace lookup. */
export const WorkerstationHost = /* @__PURE__ */ (() => Schema.Union([
  Schema.Struct({ kind: Schema.Literal("apex") }),
  Schema.Struct({ kind: Schema.Literal("station"), stationId: StationId }),
  Schema.Struct({ kind: Schema.Literal("worker"), stationId: StationId, workerName: WorkerHostLabel }),
  Schema.Struct({ kind: Schema.Literal("version"), stationId: StationId, workspaceId: WorkerHostLabel, version: VersionLabel })
]))();
export type WorkerstationHost = typeof WorkerstationHost.Type;
const decodeHost = /* @__PURE__ */ (() => Schema.decodeUnknownOption(WorkerstationHost))();

/**
 * Classify a request hostname against the apex. Returns null when the hostname
 * is not the apex or one of its labels (localhost, nested origins, etc.).
 */
function hostnameParts(hostname: string): unknown {
  const host = hostname.toLowerCase();
  if (host === WORKERSTATION_APEX_HOST) return { kind: "apex" };
  if (!host.endsWith(`.${WORKERSTATION_APEX_HOST}`)) return null;
  const label = host.slice(0, -(WORKERSTATION_APEX_HOST.length + 1));
  if (!label || label.includes(".")) return null;
  const parts = label.split(WORKERSPACE_HOST_SEPARATOR);
  if (parts.length === 1) {
    return { kind: "station", stationId: label };
  }
  if (parts.length === 2) {
    const [workerName, stationId] = parts;
    return { kind: "worker", stationId, workerName };
  }
  if (parts.length === 3) {
    const [version, workspaceId, stationId] = parts;
    return { kind: "version", stationId, workspaceId, version };
  }
  // More than two `--` is ambiguous (and impossible given the name rules).
  return null;
}

/** Schema decoding owns every resource identifier in the classified host. */
export const parseWorkerstationHost = (hostname: string): WorkerstationHost | null =>
  Option.getOrNull(decodeHost(hostnameParts(hostname)));

/**
 * The station id addressed by a bare `<station-id>.<apex>` hostname (the editor
 * subdomain), or null. Worker subdomains (`<worker>--<station>`) return null —
 * use {@link parseWorkerstationHost} to handle those.
 */
export function stationIdFromHostname(hostname: string): string | null {
  const parsed = parseWorkerstationHost(hostname);
  return parsed?.kind === "station" ? parsed.stationId : null;
}

/** The editor subdomain for a station. */
export function stationHostname(stationId: string): string {
  return `${stationId}.${WORKERSTATION_APEX_HOST}`;
}

/** The worker's own subdomain: `<worker>--<station>.<apex>`. */
export function workerHostname(workerName: string, stationId: string): string {
  return `${workerName}${WORKERSPACE_HOST_SEPARATOR}${stationId}.${WORKERSTATION_APEX_HOST}`;
}

/** DNS bounds the combined label, including the station and separator. */
export const canUseWorkerHostname = (workerName: string, stationId: string): boolean =>
  isValidWorkerHostLabel(workerName) && Schema.is(StationId)(stationId) &&
  workerName.length + WORKERSPACE_HOST_SEPARATOR.length + stationId.length <= 63;

/** Long preview names use the existing apex path route without renaming source. */
export const workerPreviewBase = (workerName: string, stationId: string, protocol = "https:"): string =>
  canUseWorkerHostname(workerName, stationId)
    ? `${protocol}//${workerHostname(workerName, stationId)}`
    : `${protocol}//${WORKERSTATION_APEX_HOST}/${encodeURIComponent(stationId)}/${encodeURIComponent(workerName)}`;

/** A versioned workspace subdomain: `<version>--<workspace>--<station>.<apex>`. */
export function versionedWorkspaceHostname(
  version: string,
  workspaceId: string,
  stationId: string
): string {
  const sep = WORKERSPACE_HOST_SEPARATOR;
  return `${version}${sep}${workspaceId}${sep}${stationId}.${WORKERSTATION_APEX_HOST}`;
}
